import React, {useEffect, useState, useRef, useMemo} from 'react'
import {MapContainer, TileLayer, GeoJSON, Popup} from 'react-leaflet'
import styled, {keyframes} from 'styled-components'
import 'leaflet/dist/leaflet.css'
import axios from 'axios'
import {Typography} from '@material-ui/core'
import Button from '@material-ui/core/Button'

interface FundingData {
  id: string
  program_name: string
  agency_name: string
  bureau_name: string
  funding: number
  category: string
  subcategory: string
  state_name: string
  city: string | null
  county: string | null
  project_name: string
  status: string
  type: string
  'Period of Availability': string
  recipient: string
  recipient_type: string
  program_description: string
  learn_more: string
  funding_source: string
}

interface StyledLoaderProps {
  loading: boolean
}

// Define the keyframe animation for spinning
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

// Update your StyledLoader to include the animation
const StyledLoader = styled.div<StyledLoaderProps>`
  display: ${(props) => (props.loading ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 1000;

  img {
    animation: ${spin} 4s linear infinite; // Apply the spinning animation to the image
  }
`

const FilterButton = styled.button`
  margin-right: 10px;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #5e6278;
  color: white;
  cursor: pointer;
  &:hover {
    background-color: #4e5368;
  }
  &:active {
    background-color: #3d4358;
  }
`

interface SelectedFeature {
  county: string
  programs: {
    type: string
    agency_name: string
    program_name: string
    project_name: string
    funding: number
    learn_more: string
  }[]
}

const StyledContainer = styled.div``

const StyledMapContainer = styled.div`
  flex-grow: 1;
`

////////////////////////////////

const StyledProgramList = styled.ul`
  list-style-type: disc;
  padding-left: 30px;
  font-size: 1.2rem;

  li {
    margin-bottom: 12px;
  }
`

const StyledLegendItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 1.2rem;
`

const StyledLegendColorBox = styled.div`
  width: 40px;
  height: 40px;
  margin-right: 10px;
`

const StyledInfoContainer = styled.div`
  width: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 8px;
  border: 1px solid black;
  margin-bottom: 20px;
`

const StyledSideBar = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 30px;
  font-size: 1.4rem;
`

const StyledSelect = styled.select`
  width: 100%;
  padding: 10px;
  margin-bottom: 20px;
  font-size: 1.2rem;
`

const MapView: React.FC = () => {
  const mapStyle = {height: '100vh', width: '100%', margin: '0 auto'}
  const [loading, setLoading] = useState(true)
  const [fundingData, setFundingData] = useState<FundingData[]>([])
  const [countyData, setCountyData] = useState(null)
  const geoUrl = process.env.PUBLIC_URL + '/colorado-counties.geojson'
  const [onselect, setOnselect] = useState<SelectedFeature | null>(null)
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null)
  const selectedCountyRef = useRef(selectedCounty)
  const [selectedLayer, setSelectedLayer] = useState(null)
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null)
  const [uniqueSubcategories, setUniqueSubcategories] = useState<string[]>([])
  const [filteredData, setFilteredData] = useState<FundingData[]>([])
  const [fundingType, setFundingType] = useState<string>('all')

  const handleFundingTypeChange = (type: string) => {
    setFundingType(type)

    // New logic to filter data based on funding type
    if (type === 'IRA') {
      const filtered = fundingData.filter((item) => item.funding_source === 'IRA')
      setFilteredData(filtered)
      // console.log('IRA button clicked, filtered data:', filtered)
    } else if (type === 'BIL') {
      const filtered = fundingData.filter((item) => item.funding_source === 'BIL')
      setFilteredData(filtered)
      // console.log('BIL button clicked, filtered data:', filtered)
    } else {
      // Default case when 'all' is selected
      setFilteredData(fundingData)
      // console.log('All data selected:', fundingData)
    }
  }

  useEffect(() => {
    const subcategories = Array.from(new Set(fundingData.map((data) => data.subcategory)))
    setUniqueSubcategories(subcategories)
  }, [fundingData])

  // Function to handle subcategory change
  const handleSubcategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSubcategory(event.target.value || null)
  }

  // Filtered data based on selected subcategory
  // Define fetchFundingData outside of any useEffect
  const [fundingSourceFilter, setFundingSourceFilter] = useState<string>('ALL')

  // Adjust fetchFundingData to consider the fundingSourceFilter
  const fetchFundingData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/bli`)
      let data = response.data

      if (fundingSourceFilter !== 'ALL') {
        data = data.filter((item) => item.funding_source === fundingSourceFilter)
      }

      setFundingData(data)
      // console.log('data', data)
      setFilteredData(data) // Update the filtered data
    } catch (error) {
      console.error('Error fetching funding data:', error)
    }
  }

  // Call fetchFundingData whenever fundingSourceFilter changes
  useEffect(() => {
    fetchFundingData()
  }, [fundingSourceFilter])

  useEffect(() => {
    let data = [...fundingData] // Clone the data to prevent mutations

    if (selectedSubcategory) {
      data = data.filter((d) => d.subcategory === selectedSubcategory)
    }

    setFilteredData(data)
  }, [selectedSubcategory, fundingData])

  // Now, fetchCountyData
  const fetchCountyData = async () => {
    try {
      const response = await axios.get(geoUrl)
      setCountyData(response.data)
    } catch (error) {
      console.error('Error fetching county data:', error)
    }
  }

  useEffect(() => {
    // Now you can use fetchFundingData here
    Promise.all([fetchFundingData(), fetchCountyData()])
      .then(() => setLoading(false))
      .catch((error) => console.error('Error:', error))
  }, [])

  const getFundingForCounty = (countyName: string) => {
    const countyData = filteredData.find((data: FundingData) => data.county === countyName)
    return countyData ? countyData.funding : 0
  }

  const onEachFeature = (feature, layer) => {
    // Save the original style
    layer.defaultStyle = countyStyle(feature)

    layer.on({
      click: (e) => {
        const countyName = feature.properties.name
        const fundingForCounty = filteredDataRef.current
          .filter((data) => data.county === countyName)
          .reduce((total, current) => total + current.funding, 0)

        // console.log(`Clicked on ${countyName}, Funding: ${fundingForCounty}`)
        // console.log(`Current filteredData from Ref:`, filteredDataRef.current)

        layer
          .bindPopup(
            `<strong>${countyName}</strong><br>Funding: $${fundingForCounty.toLocaleString()}`
          )
          .openPopup()

        // Then handle any additional logic
        selectCounty(e)
      },
    })
  }

  const filteredDataRef = useRef(filteredData)
  useEffect(() => {
    filteredDataRef.current = filteredData
  }, [filteredData])

  const selectCounty = (e: any) => {
    const layer = e.target
    const countyName = layer.feature.properties.name

    setSelectedLayer(layer)
    setSelectedCounty(countyName)

    const fundingDataForCounty = filteredData.filter(
      (data: FundingData) => data.county === countyName
    )

    if (fundingDataForCounty && fundingDataForCounty.length > 0) {
      const programs = fundingDataForCounty.map((data) => ({
        type: data.type,
        agency_name: data.agency_name,
        program_name: data.program_name,
        project_name: data.project_name,
        funding: data.funding,
        learn_more: data.learn_more,
      }))

      setOnselect({
        county: countyName,
        programs: programs,
      })
    } else {
      setOnselect({
        county: countyName,
        programs: [],
      })
    }
  }

  const getFundingColor = (funding: number) => {
    if (funding > 100000000) {
      return '#16384A' // Dark blue
    } else if (funding > 10000000) {
      return '#276A5D' // Intermediate color
    } else if (funding > 1000000) {
      return '#3A8B70' // Intermediate color
    } else if (funding > 500000) {
      return '#4EAB83' // Intermediate color
    } else if (funding > 100000) {
      return '#61CC96' // Intermediate color
    } else if (funding > 0) {
      return '#75EDAA' // Intermediate color
    } else {
      return '#BAF6D4' // Green for no funding
    }
  }

  const countyStyle = (feature: any) => {
    const funding = getFundingForCounty(feature.properties.name)
    return {
      fillColor: getFundingColor(funding),
      weight: 1,
      opacity: 1,
      color: 'black',
      dashArray: '3',
      fillOpacity: selectedCounty === feature.properties.name ? 1 : 0.8,
    }
  }

  const Legend: React.FC<{threshold: number}> = ({threshold}) => {
    const legendItems = [
      {color: '#16384A', label: '> $100,000,000'},
      {color: '#276A5D', label: '> $10,000,000'},
      {color: '#3A8B70', label: '> $1,000,000'},
      {color: '#4EAB83', label: '> $500,000'},
      {color: '#61CC96', label: '> $100,000'},
      {color: '#75EDAA', label: '> $0'},
      {color: '#BAF6D4', label: 'No funding'},
    ]

    return (
      <StyledInfoContainer>
        {legendItems.map((item, index) => (
          <StyledLegendItem key={index}>
            <StyledLegendColorBox style={{backgroundColor: item.color}}></StyledLegendColorBox>
            <div>{item.label}</div>
          </StyledLegendItem>
        ))}
      </StyledInfoContainer>
    )
  }

  return (
    <div className='card'>
      <StyledContainer className='container'>
        <h1>Colorado IIJA and IRA Funding Per County</h1>
        {loading ? (
          <StyledLoader loading={loading}>
            <img src={process.env.PUBLIC_URL + '/media/logos/LumWheel2.png'} alt='Luminance logo' />
            <span>Loading ...</span>
          </StyledLoader>
        ) : (
          <>
            {/* Row 1: Map and SideBar */}
            <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
              {/* Map Column */}
              <div className='col-xl-8'>
                <StyledMapContainer>
                  <MapContainer
                    center={[38, -105.15887]}
                    scrollWheelZoom={false} // Disable mouse wheel zoom
                    zoomControl={false} // Disable zoom controls
                    zoom={7.3}
                    style={mapStyle}
                    maxZoom={10}
                  >
                    <TileLayer
                      attribution='Map tiles by Carto, under CC BY 3.0. Data by OpenStreetMap, under ODbL.'
                      url='https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png'
                    />
                    {countyData && (
                      <GeoJSON
                        key={filteredData.length} // Add this line
                        data={countyData}
                        style={countyStyle}
                        onEachFeature={onEachFeature}
                      />
                    )}
                  </MapContainer>
                </StyledMapContainer>
              </div>
              <div className='col-xl-4'>
                <StyledSideBar>
                  {/* Funding Source Filter Buttons */}
                  <StyledSideBar>
                    {/* MUI Funding Source Filter Buttons */}
                    <div>
                      <Button
                        style={{backgroundColor: fundingType === 'IRA' ? '#61CC96' : undefined}} // Color for IRA
                        variant={fundingType === 'IRA' ? 'contained' : 'outlined'}
                        onClick={() => handleFundingTypeChange('IRA')}
                        size='medium'
                        className='mr-2'
                      >
                        IRA Funds
                      </Button>
                      <Button
                        style={{backgroundColor: fundingType === 'BIL' ? '#00A1C1' : undefined}} // Color for IIJA (BIL)
                        variant={fundingType === 'BIL' ? 'contained' : 'outlined'}
                        onClick={() => handleFundingTypeChange('BIL')}
                        size='medium'
                        className='mr-2'
                      >
                        IIJA (BIL) Funds
                      </Button>
                    </div>

                    {/* ... rest of the sidebar content ... */}
                  </StyledSideBar>
                  <br />
                  {fundingType !== 'IRA' && (
                    <StyledSelect
                      onChange={handleSubcategoryChange}
                      value={selectedSubcategory || ''}
                    >
                      <option value=''>All Subcategories</option>
                      {uniqueSubcategories.map((subcategory, index) => (
                        <option key={index} value={subcategory}>
                          {subcategory}
                        </option>
                      ))}
                    </StyledSelect>
                  )}

                  <Legend threshold={10000000} />
                  {/* Program Details */}
                  <StyledInfoContainer>
                    <p style={{fontWeight: 'bold'}}>Programs:</p>
                    {onselect ? (
                      onselect.programs.length > 0 ? (
                        <StyledProgramList>
                          {onselect.programs.map((program, index) => (
                            <li key={index}>{program.project_name}</li>
                          ))}
                        </StyledProgramList>
                      ) : (
                        <p>No Programs in this county.</p>
                      )
                    ) : (
                      <p>Please select a county</p>
                    )}
                  </StyledInfoContainer>
                </StyledSideBar>
              </div>
            </div>
          </>
        )}
        <Typography variant='body2' align='center' gutterBottom style={{marginTop: '1em'}}>
          Build.gov (2024). Bipartisan Infrastructure Law (BIL) Maps Dashboard. Retrieved from{' '}
          <a
            href='https://d2d.gsa.gov/report/bipartisan-infrastructure-law-bil-maps-dashboard'
            target='_blank'
            rel='noopener noreferrer'
          >
            https://d2d.gsa.gov/report/bipartisan-infrastructure-law-bil-maps-dashboard
          </a>
          , Date Retrieved 03/20/2024
        </Typography>
      </StyledContainer>
    </div>
  )
}

export default MapView
